import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import {
  updateDevicesPageSelectedDeviceGroupName,
  addToastNotification,
  deleteToastNotification,
  doDeviceGroupUpdate,
  doUpdateAllUserDeviceGroups,
  doDeviceGroupDelete,
  setLocalNameFilter,
  doEditSingleDeviceGroup,
  removeFilter,
  addFilter,
  parseAllSearchFilters,
  setIsDefaultDeviceGroupInitialized
} from 'redux/actions';
import { LOADING_CIRCLE, SPECIAL_CHARS_FORMAT } from 'common/utils/constants';
import { isObjectEmpty, groupNameInputValidator, trimAndReplaceSpaces } from 'common/utils/helpers';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import { IconLoader } from 'common/IconLoader';
import './ManageDevicesListsModal.css';
import {
  createActiveFiltersSelector,
  selectDeviceGroups,
  selectSelectedDeviceGroupName,
  selectToastNotifications
} from 'redux/selectors';
import ToastNotificationActionTypes from 'components/shared/ToastNotifications/constants';

export const BulkManageDevicesLists = (props) => {
  const {
    activeTab,
    selectedDeviceGroupName,
    localEditModeList,
    setLocalEditModeList,
    setLocalSelectedDevicesList,
    setLocalSelectedDeviceGroupName,
    setLocalSelectedDevicesDictionary
  } = props;
  const selectActiveFilters = useMemo(() => createActiveFiltersSelector(activeTab), [activeTab]);

  const [nameLabelValue, setNameLabelValue] = useState('');
  const [, setErrorBoxText] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [renderKey, setRenderKey] = useState(0);
  const [actionPending, setActionPending] = useState(false);
  const [, setClickedDeviceGroupId] = useState(null);
  const deviceGroups = useSelector(selectDeviceGroups);
  const toastNotifications = useSelector(selectToastNotifications);
  const currentSelectedDeviceGroupName = useSelector(selectSelectedDeviceGroupName);
  const filterBody = useSelector(selectActiveFilters);
  const dispatch = useDispatch();

  const dismissUndoActionToast = (toastNotifications) => {
    !isObjectEmpty(toastNotifications) &&
      Object.keys(toastNotifications).forEach((toastId) => {
        if (toastNotifications[toastId].actionType === ToastNotificationActionTypes.UNDO_REMOVED_DEVICE_LIST) {
          dispatch(deleteToastNotification(toastId));
        }
      });
  };

  const applyNameLabelChanges = (evt, currentGroupName) => {
    let targetString = evt.target.value;

    setNameLabelValue(targetString);

    if (targetString === currentGroupName) setLocalError(null);

    setRenderKey(renderKey + 1);
  };

  const applyDeviceName = (groupName) => {
    let existingGroupNames = Object.keys(deviceGroups);
    let trimmedNameLabelValue = trimAndReplaceSpaces(nameLabelValue);
    if (
      trimmedNameLabelValue &&
      !existingGroupNames.includes(trimmedNameLabelValue) &&
      trimmedNameLabelValue.length <= 50 &&
      !SPECIAL_CHARS_FORMAT.test(trimmedNameLabelValue)
    ) {
      setActionPending(true);
      let newDeviceGroups = {};
      let deviceGroup = deviceGroups && deviceGroups[groupName];
      let { name, deviceGroupId, userId, devices } = deviceGroup;
      Object.keys(deviceGroups).forEach((name) => {
        if (name === groupName) {
          newDeviceGroups[trimmedNameLabelValue] = { ...deviceGroup, name: trimmedNameLabelValue };
        } else {
          newDeviceGroups[name] = deviceGroups[name];
        }
      });
      let cb_success = () => {
        setActionPending(false);
        let copy = JSON.parse(JSON.stringify(localEditModeList));
        copy[groupName] = false;
        setLocalEditModeList(copy);
        setNameLabelValue('');
        setLocalError(null);
        if (groupName === selectedDeviceGroupName) {
          dispatch(updateDevicesPageSelectedDeviceGroupName(trimmedNameLabelValue));
        }
        dismissUndoActionToast(toastNotifications);
        dispatch(doUpdateAllUserDeviceGroups(newDeviceGroups));
      };
      let cb_failed = () => {
        setActionPending(false);
        dispatch(
          addToastNotification({
            id: v4(),
            title: name,
            description: ` is failed to change its name. Try again`,
            groupName: name
          })
        );
      };

      dispatch(
        doDeviceGroupUpdate(
          deviceGroupId,
          trimmedNameLabelValue,
          deviceGroup.default,
          devices,
          userId,
          cb_success,
          cb_failed
        )
      );
    } else if (existingGroupNames.includes(trimmedNameLabelValue) && trimmedNameLabelValue !== groupName) {
      setLocalError('The name already exists. Please pick another name.');
    } else {
      setLocalError(groupNameInputValidator(trimmedNameLabelValue, existingGroupNames, []));
    }
  };

  const handleMakeDefault = (deviceGroup) => {
    setActionPending(true);
    const { name, deviceGroupId, devices, userId } = deviceGroup;
    let newDefault = !deviceGroup.default;
    setClickedDeviceGroupId(deviceGroupId);
    let cb_success = () => {
      setActionPending(false);
      dispatch(updateDevicesPageSelectedDeviceGroupName(newDefault ? name : null));
      newDefault && dispatch(setIsDefaultDeviceGroupInitialized(true));
      dispatch(newDefault ? addFilter(activeTab, { deviceId: devices }) : removeFilter(activeTab, 'deviceId'));
      dismissUndoActionToast(toastNotifications);
      dispatch(
        addToastNotification({
          id: v4(),
          title: name,
          description: newDefault ? 'is set as the new default list.' : 'is unset as the default list.',
          groupName: name
        })
      );
    };
    let cb_failed = () => {
      setActionPending(false);
      dispatch(
        addToastNotification({
          id: v4(),
          title: deviceGroup.name,
          description: `Failed to set default for`,
          groupName: name,
          reverse: true
        })
      );
    };
    dispatch(doDeviceGroupUpdate(deviceGroupId, name, newDefault, devices, userId, cb_success, cb_failed));
  };

  const handleRemoveDeviceGroup = async (groupName, deviceGroupDevices, deviceGroupId) => {
    setActionPending(true);
    setClickedDeviceGroupId(deviceGroupId);
    let newDeviceGroups = JSON.parse(JSON.stringify(deviceGroups));
    let deletedDeviceGroup = newDeviceGroups[groupName];
    delete newDeviceGroups[groupName];
    (await toastNotifications) &&
      Object.keys(toastNotifications).forEach((toastId) => {
        if (toastNotifications[toastId].groupName === groupName) {
          dispatch(deleteToastNotification(toastId));
        }
      });
    let cb_success = () => {
      dispatch(doUpdateAllUserDeviceGroups(newDeviceGroups));

      if (currentSelectedDeviceGroupName === groupName) {
        let newFilterDictionary = { ...filterBody };
        newFilterDictionary['deviceId'] && delete newFilterDictionary['deviceId'];
        newFilterDictionary['properties.reported.productType'] &&
          delete newFilterDictionary['properties.reported.productType'];
        dispatch(updateDevicesPageSelectedDeviceGroupName(null));
        dispatch(parseAllSearchFilters(activeTab, newFilterDictionary, false, true));
        // dispatch(setFilterDictionary(newFilterDictionary));
        dispatch(setLocalNameFilter(''));
      }
      setActionPending(false);

      dispatch(
        addToastNotification({
          id: v4(),
          title: groupName,
          description: 'removed.',
          actionMessage: 'Undo',
          actionType: ToastNotificationActionTypes.UNDO_REMOVED_DEVICE_LIST,
          deletedDeviceList: deviceGroupDevices,
          groupName: groupName,
          deletedDeviceGroup: deletedDeviceGroup,
          callback: (isActionPending) => {
            setActionPending(isActionPending);
            dispatch(doEditSingleDeviceGroup(groupName, deletedDeviceGroup));
            if (groupName === currentSelectedDeviceGroupName) {
              dispatch(updateDevicesPageSelectedDeviceGroupName(deletedDeviceGroup.name));
              dispatch(addFilter(activeTab, { deviceId: deletedDeviceGroup.devices }));
            }
          }
        })
      );
    };
    let cb_failed = () => {
      setActionPending(false);
      dispatch(
        addToastNotification({
          id: v4(),
          title: groupName,
          description: 'Failed to remove ',
          deletedDeviceList: deviceGroupDevices,
          groupName: groupName,
          reverse: true
        })
      );
    };

    dispatch(doDeviceGroupDelete(deviceGroupId, cb_success, cb_failed));
  };

  let overlay = null;
  if (actionPending) {
    overlay = <div className="Global_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  }

  return (
    <div className="BulkManageDevicesLists">
      {overlay}
      <div className="BulkManageDevicesLists_HeaderDiv">
        <div className="BulkManageDevicesLists_HeaderDiv_Title">Manage lists</div>
      </div>
      {/* {numOfCheckedLists > 1 && (
        <div className="Global_ThinBlueButton BulkManageDevicesLists_MergeListsButton">
          <IconAndText
            img={IconLoader.icon_add_devices}
            hoverImg={IconLoader.icon_add_devices}
            text={'Merge lists'}
            tooltip={'Merge selected lists'}
          />
        </div>
      )} */}
      <div className="BulkManageDevicesLists_Container">
        <PerfectScrollbar>
          {!isObjectEmpty(deviceGroups) ? (
            Object.keys(deviceGroups).map((groupName, idx) => {
              let deviceGroup = deviceGroups[groupName];
              let deviceGroupDevices = deviceGroup.devices;
              let isDefault = deviceGroup.default;

              return (
                <div
                  key={idx}
                  className="BulkManageDevicesLists_ItemDiv"
                  onClick={() => {
                    setClickedDeviceGroupId(deviceGroup.deviceGroupId);
                    let newLocalSelectedDevicesDictionary = {};
                    deviceGroupDevices.forEach((deviceId) => {
                      newLocalSelectedDevicesDictionary[deviceId] = { checked: true, matched: true };
                    });
                    setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
                  }}
                >
                  <div className="BulkManageDevicesLists_Item_ActionsRowLeft">
                    {/* <Checkbox
                      isChecked={isChecked}
                      onChange={(event) => {
                        localCheckList[groupName] = !isChecked;
                        let copy = JSON.parse(JSON.stringify(localCheckList));
                        setLocalCheckList(copy);
                        event.stopPropagation(); //stop default row click stuff from happening
                      }}
                    /> */}
                    {!localEditModeList[groupName] && (
                      <div className="BulkManageDevicesLists_Item_ListName_Box">
                        <div
                          className="BulkManageDevicesLists_Item_ListName"
                          data-tip={groupName}
                          onClick={() => {
                            setLocalSelectedDevicesList(deviceGroupDevices);
                            setLocalSelectedDeviceGroupName(groupName);
                            let newLocalEditModeList = JSON.parse(JSON.stringify(localEditModeList));
                            Object.keys(newLocalEditModeList).forEach((groupName) => {
                              if (newLocalEditModeList[groupName]) {
                                newLocalEditModeList[groupName] = false;
                              }
                            });
                            setLocalEditModeList(newLocalEditModeList);
                          }}
                        >
                          {groupName}
                        </div>

                        <div className="nameLabelEditButton">
                          <SvgAF
                            img={IconLoader.icon_edit_name}
                            hover={IconLoader.icon_edit_name_hover}
                            onClick={() => {
                              let newLocalEditModeList = JSON.parse(JSON.stringify(localEditModeList));
                              Object.keys(newLocalEditModeList).forEach((name) => {
                                if (groupName === name) {
                                  newLocalEditModeList[groupName] = true;
                                } else {
                                  newLocalEditModeList[name] = false;
                                }
                              });
                              newLocalEditModeList[groupName] = true;
                              setLocalEditModeList(newLocalEditModeList);
                              setNameLabelValue(groupName);
                              setErrorBoxText(null);
                              setLocalError(null);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {localEditModeList[groupName] && (
                      <div className="BulkManageDevicesLists_Item_ListName_Box">
                        <div className="BulkManageDevicesLists_Item_NameLabelEditor">
                          <input
                            onChange={(evt) => applyNameLabelChanges(evt, groupName)}
                            value={nameLabelValue}
                            type="text"
                          />
                        </div>
                        <div className="BulkManageDevicesLists_Item_EditButtonSection">
                          {trimAndReplaceSpaces(nameLabelValue) === groupName ? (
                            <SvgAF img={IconLoader.icon_commit_disabled} />
                          ) : (
                            <SvgAF img={IconLoader.icon_commit_edit} onClick={() => applyDeviceName(groupName)} />
                          )}
                          <SvgAF
                            img={IconLoader.icon_cancel_edit}
                            onClick={() => {
                              let copy = JSON.parse(JSON.stringify(localEditModeList));
                              copy[groupName] = false;
                              setLocalEditModeList(copy);
                              setNameLabelValue('');
                              setErrorBoxText(null);
                              setLocalError(null);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {localEditModeList[groupName] && localError && (
                      <div className="BulkManageDevicesLists_ErrorMessage">{localError}</div>
                    )}
                  </div>
                  <div className="BulkManageDevicesLists_Item_ActionsRowRight">
                    <SvgAF
                      img={isDefault ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default}
                      hover={isDefault ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default_hover}
                      onClick={() => handleMakeDefault(deviceGroup)}
                    />
                  </div>
                  <div className="BulkManageDevicesLists_Item_DeleteButton">
                    <SvgAF
                      img={IconLoader.icon_delete_list}
                      hover={IconLoader.icon_delete_list_hover}
                      onClick={() => {
                        handleRemoveDeviceGroup(groupName, deviceGroupDevices, deviceGroup.deviceGroupId);
                        if (groupName === currentSelectedDeviceGroupName) {
                          dispatch(removeFilter(activeTab, 'deviceId'));
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="BulkManageDevicesLists_NoListsCreatedDiv">No list has been created.</div>
          )}
        </PerfectScrollbar>
      </div>
    </div>
  );
};
